import type { OfferProduct } from '@cod/config-schema';
import {
  Box,
  Button,
  Checkbox,
  Input,
  Modal,
  Spinner,
  Text,
  Thumbnail,
} from '@nimbus-ds/components';
import { SearchIcon } from '@nimbus-ds/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getProducts } from '../api/client.js';

const PER_PAGE = 50;

/**
 * Product picker modal for the offer editor. Searches the store's products (server-side
 * proxy to the Nuvemshop API) with paging, lets the merchant multi-select, and returns the
 * chosen products as OfferProduct[]. Products already on the offer come pre-checked.
 */
export function ProductPicker({
  open,
  initialSelected,
  onAdd,
  onClose,
}: {
  open: boolean;
  initialSelected: OfferProduct[];
  onAdd: (products: OfferProduct[]) => void;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState<OfferProduct[]>([]);
  const [selected, setSelected] = useState<Map<number, OfferProduct>>(new Map());
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Seed the selection from the offer's current products each time the modal opens.
  useEffect(() => {
    if (open) setSelected(new Map(initialSelected.map((p) => [p.id, p])));
    // initialSelected identity changes per render; key only on `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch page 1 on open and whenever the (debounced) query changes.
  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => {
      setLoading(true);
      setError(false);
      getProducts(query, 1)
        .then((items) => {
          setProducts(items);
          refreshSelected(items);
          setPage(1);
          setHasMore(items.length === PER_PAGE);
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [open, query]);

  const loadMore = (): void => {
    const next = page + 1;
    setLoading(true);
    getProducts(query, next)
      .then((items) => {
        setProducts((prev) => [...prev, ...items]);
        refreshSelected(items);
        setPage(next);
        setHasMore(items.length === PER_PAGE);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  // Refresh already-selected products with freshly fetched objects so they pick up data the
  // saved offer never captured (notably `variants`). Without this, a product already on the
  // offer keeps its stale `initialSelected` object — with no variants — when re-confirmed.
  const refreshSelected = (items: OfferProduct[]): void =>
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      for (const it of items) {
        if (next.has(it.id)) {
          next.set(it.id, it);
          changed = true;
        }
      }
      return changed ? next : prev;
    });

  const toggle = (product: OfferProduct): void =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(product.id)) next.delete(product.id);
      else next.set(product.id, product);
      return next;
    });

  const confirm = (): void => {
    onAdd([...selected.values()]);
    onClose();
  };

  return (
    <Modal open={open} onDismiss={onClose}>
      <Modal.Header title={t('productPicker.title')} />
      <Modal.Body padding="none">
        <Box marginBottom="4">
          <Input
            id="product-picker-search"
            placeholder={t('productPicker.searchPlaceholder')}
            value={query}
            append={<SearchIcon />}
            onChange={(e) => setQuery((e.target as HTMLInputElement).value)}
          />
        </Box>

        {error ? (
          <Box display="flex" justifyContent="center" paddingY="6">
            <Text color="danger-textLow">{t('productPicker.error')}</Text>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap="1" maxHeight="360px" overflowY="auto">
            {products.map((product) => (
              <Box
                key={product.id}
                display="flex"
                alignItems="center"
                gap="3"
                paddingX="2"
                paddingY="2"
                onClick={() => toggle(product)}
                cursor="pointer"
              >
                <Checkbox
                  name={`product-${product.id}`}
                  checked={selected.has(product.id)}
                  onChange={() => toggle(product)}
                />
                <Box width="40px" flex="0 0 auto">
                  <Thumbnail
                    width="40px"
                    aspectRatio="1/1"
                    alt={product.name}
                    src={product.image ?? ''}
                  />
                </Box>
                <Text>{product.name}</Text>
              </Box>
            ))}

            {loading && (
              <Box display="flex" justifyContent="center" paddingY="4">
                <Spinner size="small" />
              </Box>
            )}

            {!loading && products.length === 0 && (
              <Box display="flex" justifyContent="center" paddingY="6">
                <Text color="neutral-textLow">{t('productPicker.empty')}</Text>
              </Box>
            )}

            {!loading && hasMore && (
              <Box display="flex" justifyContent="center" paddingY="2">
                <Button onClick={loadMore}>{t('productPicker.loadMore')}</Button>
              </Box>
            )}
          </Box>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Text color="neutral-textLow">
            {t('productPicker.selectedCount', { count: selected.size })}
          </Text>
          <Box display="flex" gap="2">
            <Button onClick={onClose}>{t('productPicker.cancel')}</Button>
            <Button appearance="primary" onClick={confirm} disabled={selected.size === 0}>
              {t('productPicker.add')}
            </Button>
          </Box>
        </Box>
      </Modal.Footer>
    </Modal>
  );
}
